import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  RegisterService,
  TCountryEquipmentFormGroup,
  TRegisterEquipmentFormGroup,
} from 'src/app/core/services/register/register.service';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { EquipmentInputsSettings } from 'src/app/shared/models/constants/equipment-input-settings';
import { CountryList } from 'src/app/shared/models/dtos/register-form/country-list/country-list';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { DialogModalData } from '../dialog-modal/dialog-modal.component';
import { TranslationService } from 'src/app/core/services/translation/translation.service';

export type TBusinessEquipmentFormPhase =
  | 'EPSON_UNREGISTERED_PENDING'
  | 'LNR_REGISTER';

@Component({
  selector: 'app-business-equipment-form',
  templateUrl: './business-equipment-form.component.html',
  styleUrls: ['./business-equipment-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Business equipment section of user register
 */
export class BusinessEquipmentFormComponent implements OnInit, OnDestroy {
  @Input() equipmentData!: BusinessEquipmentFormObject;
  @Input() registerEquipmentFormGroup!: TRegisterEquipmentFormGroup;
  @Input() countries: CountryList[] = [];
  @Input() feedback?: 'registered' | 'eligible' | '';
  @Input() isRegistration: boolean = false;
  @Input() countryFormGroup!: TCountryEquipmentFormGroup;
  @Input() loading = false;
  @Input() isAddingEquipment = false;
  @Input() privacyLink?: string;
  @Input() showAddingEquipmentButton: boolean = true;
  @Output() isAddingEquipmentChange = new EventEmitter<boolean>();
  @Output() deleteEquipmentAction = new EventEmitter<FormGroup>();

  businessEquipmentFormGroup!: UntypedFormGroup;
  formArray!: UntypedFormArray;
  serverError = false;
  equipmentExistsError = false;
  private readonly subsink = new Subscription();
  private _phase: TBusinessEquipmentFormPhase = 'LNR_REGISTER';
  today = new Date().toISOString();
  labels = {
    equipmentRemoval: 'equipment-removal',
    equipmentRemovalConfirm: 'equipment-removal-confirm',
    cancel: 'cancel',
    removeEquipment2: 'remove-equipment-2',
  };

  /**
   * @return {TBusinessEquipmentFormPhase}
   */
  @Input()
  get phase(): TBusinessEquipmentFormPhase {
    return this._phase;
  }

  /**
   * @param {TBusinessEquipmentFormPhase} value
   */
  set phase(value: TBusinessEquipmentFormPhase) {
    this._phase = value;
    if (this.pendingProductRegistration && this.formArray) {
      this.formArray.disable();
    }
  }

  /**
   * Equipment form getter
   */
  get equipmentsFormGroup() {
    return this.businessEquipmentFormGroup.get(
      'equipments'
    ) as UntypedFormArray;
  }

  /**
   * Uses formBuilder
   * @constructor
   */
  constructor(
    private registerService: RegisterService,
    private readonly dialogService: DialogService,
    readonly translationService: TranslationService
  ) {}

  /**
   * Init form
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);
    this.businessEquipmentFormGroup = this.equipmentData.formGroup;
    this.formArray = this.businessEquipmentFormGroup.get(
      'equipments'
    ) as UntypedFormArray;
    this.getProvinces();
    if (this.formArray.value[0].serial.length === 0) {
      this.registerEquipmentFormGroup.reset();
    }
    this.feedback = '';
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Adding the fields to a form array
   */
  addTuple() {
    this.isAddingEquipment = true;
    this.isAddingEquipmentChange.emit(this.isAddingEquipment);
    this.formArray.push(this.registerService.createEquipmentFormTuple());
  }

  /**
   * @return {boolean}
   */
  getCountryFieldState(): boolean {
    return this.registerService.disableCountryField;
  }

  /**
   * Removing the last fields index from a form array
   * @param {number | FormGroup} param - Index or form control to remove
   */
  removeTuple(param?: number | FormGroup) {
    if (typeof param === 'number') {
      let removeIndex = this.equipmentsFormGroup.length - 1;
      if (param !== null) {
        removeIndex = param;
      }
      this.formArray.removeAt(removeIndex);

      const hasUnsavedItem = this.formArray.controls.some(
        (control) => control.get('id')?.value == null && control.enabled
      );

      if (!hasUnsavedItem) {
        this.isAddingEquipment = false;
        this.isAddingEquipmentChange.emit(this.isAddingEquipment);
      }
    }
    if (param instanceof FormGroup) {
      this.deleteEquipmentAction.emit(param);
    }
  }

  /**
   * Deletes equipment on delete button click
   * @param {number | FormGroup} param - Index or form control to remove
   */
  deleteEquipment(param?: number | FormGroup) {
    if (typeof param !== 'number') {
      const dialogData: DialogModalData = {
        title: this.labels.equipmentRemoval,
        content: this.labels.equipmentRemovalConfirm,
        failButtonText: this.labels.cancel,
        successButtonText: this.labels.removeEquipment2,
        successButtonIcon: 'trash-2-outline',
        isRedButton: true,
        isNewDesign: true,
      };

      this.dialogService.openDialog(dialogData, () => {
        this.removeTuple(param);
      });
    } else {
      this.removeTuple(param);
    }
  }

  /**
   * Getting the fields from the form group
   * @param {number} index
   * @return {FormGroup} index
   */
  getTuplesFormGroup(index: number): UntypedFormGroup {
    const formGroup = this.formArray.at(index) as UntypedFormGroup;

    return formGroup;
  }

  /**
   * Verifies number of inputs
   * @return {boolean}
   */
  hasReachedMaxInput(): boolean {
    const numberOfTuples = this.formArray.controls.length;
    return numberOfTuples >= EquipmentInputsSettings.MAX_EQUIPMENT_INPUT;
  }

  /**
   * @param {number} index
   * @param {string} controlName
   * @return {FormControl}
   */
  getControl(index: number, controlName: string): FormControl {
    return this.getTuplesFormGroup(index).get(controlName) as FormControl;
  }

  /**
   * Enables or disables close form action
   * @param {number} index
   * @return {boolean}
   */
  canRemoveTuple(index: number): boolean {
    const tuple = this.formArray.at(index);
    return (
      this.phase === 'LNR_REGISTER' &&
      (this.isAddingEquipment ||
        (tuple.enabled && tuple.get('id')?.value == undefined)) &&
      this.formArray.length > 1
    );
  }

  /**
   * Changes state dropdown
   * @param {boolean} hasChanged
   * @return {any}
   */
  getProvinces(hasChanged?: boolean): any {
    const country = this.countries.find(
      (a) => a.id === this.countryFormGroup.get('country')?.value
    );

    if (country?.provinces.length === 0) {
      country?.provinces.push({ id: 0, name: 'N/A' });
      this.registerEquipmentFormGroup.controls.stateOrRegion.setValue('N/A');
    } else if (hasChanged) {
      this.registerEquipmentFormGroup.controls.stateOrRegion.setValue('');
    }

    return country?.provinces;
  }

  /**
   * Whether there is a pending equipment to be registered
   */
  get pendingProductRegistration() {
    return this.phase === 'EPSON_UNREGISTERED_PENDING';
  }

  /**
   * Decides which label feedback to use
   */
  get feedbackLabel() {
    let feedbackLabel = '';
    if (this.feedback === 'eligible') {
      feedbackLabel = 'product-eligible-for-rewards-program';
    } else if (this.feedback === 'registered') {
      feedbackLabel = 'product-successfully-registered';
    }
    return feedbackLabel;
  }

  /**
   * Decides whether business contact email input is loading or not
   */
  get businessContactEmailInputType() {
    return (
      (this.registerService.registerEquipmentFormGroup.pending && 'loading') ||
      ''
    );
  }

  /**
   * Gets controls as formGroup
   */
  get equipmentFormGroups(): FormGroup[] {
    return this.equipmentsFormGroup.controls as FormGroup[];
  }

  /**
   * check if possible
   * @return {boolean}
   */
  canAddNewEquipment(): boolean {
    if (this.loading || this.hasReachedMaxInput()) return false;
    if (this.phase === 'EPSON_UNREGISTERED_PENDING') return false;
    if (this.isAddingEquipment) return false;
    return this.formArray.controls.every(
      (control) => control.get('id')?.value != null || control.disabled
    );
  }
}
